import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useAuthStore } from "../state/authStore";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "error"; message: string };

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful email change so the parent (banner) can show
   *  a "sent" toast keyed to the new address. */
  onSuccess?: (newEmail: string) => void;
  trigger?: ReactNode;
}

/**
 * Bottom-sheet dialog to correct a typoed registration email. Open from the
 * Verify-Email-Banner. Submitting calls authStore.changeEmail; on success
 * the user.email is updated locally and the dialog closes. Conflict /
 * format errors stay inline so the user can retype.
 */
export function CorrectEmailDialog({ open, onOpenChange, onSuccess, trigger }: Props): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const changeEmail = useAuthStore((s) => s.changeEmail);
  const [value, setValue] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  // Sync local input with user.email whenever the dialog reopens or the
  // session user swaps. Prefill with the current (potentially typoed) value
  // so the user just edits a character or two.
  useEffect(() => {
    if (open) {
      setValue(user?.email ?? "");
      setStatus({ kind: "idle" });
    }
  }, [open, user?.email]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next = value.trim().toLowerCase();
    if (next.length === 0) return;
    setStatus({ kind: "submitting" });
    const result = await changeEmail(next);
    if (result.ok) {
      onSuccess?.(next);
      onOpenChange(false);
      return;
    }
    setStatus({
      kind: "error",
      message: mapError(result.reason),
    });
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 animate-fade-in" />
        <Dialog.Content
          className="
            fixed z-50 bg-elevated shadow-modal p-5 space-y-4
            left-0 right-0 bottom-0 rounded-t-sheet border-t border-outline-strong animate-sheet-up
            sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:right-auto sm:w-[440px]
            sm:-translate-x-1/2 sm:-translate-y-1/2
            sm:rounded-sheet sm:border sm:animate-slide-up
            safe-bottom sm:pb-5
          "
          data-testid="correct-email-dialog"
        >
          <div className="sm:hidden mx-auto -mt-1 mb-2 h-1.5 w-10 rounded-full bg-outline" aria-hidden />

          <Dialog.Title className="text-lg font-bold text-fg">E-Mail korrigieren</Dialog.Title>
          <Dialog.Description className="text-sm text-fg-muted leading-snug">
            Hast du dich bei der Registrierung vertippt? Gib hier die richtige E-Mail-Adresse ein —
            wir schicken einen neuen Bestätigungslink dorthin.
          </Dialog.Description>

          <form onSubmit={onSubmit} className="space-y-3" data-testid="correct-email-form">
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-fg-soft">Neue E-Mail-Adresse</span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                required
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={status.kind === "submitting"}
                data-testid="correct-email-input"
                className="w-full min-h-[48px] bg-bg border border-outline rounded-xl px-3 text-base text-fg placeholder:text-fg-faint focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 disabled:opacity-60"
              />
            </label>

            {status.kind === "error" && (
              <div
                role="alert"
                data-testid="correct-email-error"
                className="bg-danger-soft border border-danger/40 rounded-xl px-3 py-2 text-sm text-danger"
              >
                {status.message}
              </div>
            )}

            <button
              type="submit"
              disabled={status.kind === "submitting" || value.trim().length === 0}
              data-testid="correct-email-submit"
              className="w-full min-h-[52px] rounded-xl bg-accent text-accent-on text-base font-semibold shadow-card disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
            >
              {status.kind === "submitting" ? "…" : "Speichern + neue Mail senden"}
            </button>

            <Dialog.Close asChild>
              <button
                type="button"
                className="w-full min-h-[44px] px-4 py-2 rounded-lg bg-bg/40 border border-outline-soft text-fg-muted text-sm hover:text-fg hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
                data-testid="correct-email-cancel"
              >
                Abbrechen
              </button>
            </Dialog.Close>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function mapError(reason: string): string {
  switch (reason) {
    case "email_taken":
      return "Diese E-Mail ist schon registriert. Probier eine andere oder versuche dich mit dieser anzumelden.";
    case "invalid_email":
      return "Bitte gib eine gültige E-Mail-Adresse ein.";
    case "rate_limited":
      return "Zu viele Anfragen. Bitte später erneut.";
    case "already_verified":
      return "Dein Konto ist bereits bestätigt — keine Korrektur mehr nötig.";
    default:
      return "Netzwerkfehler. Versuch's gleich noch einmal.";
  }
}
